import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form } from "react-final-form";
import { isFormValid } from "shared/validators";
import { DIALOG_ACTION_TITLE, INCORRECT_INPUTS_MESSAGE, VariantLabels } from "shared/labels";
import Input from "rs-components/Input";
import TextInput from "rs-components/Input/components/TextInput";
import Dialog from "components/Dialog";
import { workspaceSimulationsSelector } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { EntitiesCreatableFromResultElement, ResultSetElementType, } from "store/rs-workspace/rs-workspace.types";
import { createEntityFromResultElementSource, setCreateEntityFromResultElementDialogData, } from "store/rs-workspace/actions/results.actions";
import { serverErrorsSelector } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { getCreateEntityRequestTemplate } from "../CreateEntityFromResultElementDialog/utils";
import { setServerError } from "store/knowledge-base-item/knowledge-base-item.actions";
import * as styles from "./styles.module.scss";
var FormFields;
(function (FormFields) {
    FormFields["NAME"] = "name";
})(FormFields || (FormFields = {}));
const CreateVariantFromResultElementDialog = ({ simulationId, adjustmentId, resultElementType, sourceId, elementId, parameterValues, }) => {
    const dispatch = useDispatch();
    const requestTemplate = useMemo(() => getCreateEntityRequestTemplate(resultElementType, EntitiesCreatableFromResultElement.Variant), [resultElementType]);
    const serverErrors = useSelector(serverErrorsSelector(requestTemplate));
    const simulations = useSelector(workspaceSimulationsSelector);
    const simulation = useMemo(() => simulations.find(({ id }) => id === simulationId), [simulationId, simulations]);
    const name = useMemo(() => {
        const variant = simulation?.state?.adjustments?.find(({ id }) => id === adjustmentId);
        const variantName = adjustmentId === 0 ? "Default variant" : variant?.name;
        return `Copy of ${variantName}`;
    }, [adjustmentId, simulation]);
    const resetErrors = useCallback(() => {
        dispatch(setServerError(null));
    }, [dispatch]);
    const handleClose = useCallback(() => {
        dispatch(setServerError(null));
        dispatch(setCreateEntityFromResultElementDialogData({ elementId: null, sourceId: null }));
    }, [dispatch]);
    const onCreateEntity = useCallback((values) => {
        const data = {
            name: values.name,
            description: "",
        };
        const basePayload = {
            resultElementType,
            elementId,
            data,
            entityType: EntitiesCreatableFromResultElement.Variant,
        };
        switch (resultElementType) {
            case ResultSetElementType.Table:
                dispatch(createEntityFromResultElementSource({
                    ...basePayload,
                    sourceId,
                    simulationName: simulation?.name ?? "",
                }));
                break;
            case ResultSetElementType.ProcessDataViewer:
                dispatch(createEntityFromResultElementSource({
                    ...basePayload,
                    sourceId,
                    simulationName: simulation?.name ?? "",
                }));
                break;
            case ResultSetElementType.SpaceViewer:
            case ResultSetElementType.LineGraph:
                dispatch(createEntityFromResultElementSource({
                    ...basePayload,
                    sourceId,
                    simulationName: simulation?.name ?? "",
                    parameterValues,
                }));
                break;
            default:
                break;
        }
    }, [dispatch, elementId, resultElementType, sourceId, parameterValues, simulation?.name]);
    return (React.createElement(Form, { onSubmit: onCreateEntity, validateOnBlur: true }, ({ handleSubmit, form, ...renderProps }) => (React.createElement(Dialog, { isOpen: true, title: VariantLabels.NEW_VARIANT, primaryButton: {
            label: DIALOG_ACTION_TITLE.OK,
            onClick: handleSubmit,
            disabled: !isFormValid(renderProps) || !!serverErrors,
            title: !isFormValid(renderProps) || !!serverErrors ? INCORRECT_INPUTS_MESSAGE : "",
        }, secondaryButtons: [{ label: DIALOG_ACTION_TITLE.CANCEL, onClick: handleClose }], onClosing: form?.reset },
        React.createElement("div", { className: styles.root },
            React.createElement(Input, { component: TextInput, name: FormFields.NAME, autoComplete: "off", classes: { component: styles.field }, label: "Name", restrictions: { IsMandatory: true, Default: name }, externalError: serverErrors?.[0]?.text, onChange: resetErrors }))))));
};
export default CreateVariantFromResultElementDialog;
